import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';

import ORBNavBar from '../../../components/common/ORBNavBar';
import ORBApi from '../../../services/ORBApi';

const TRACK_TARGET_FIELDS = [
  'report_date',
  'activity_date',
  'territory',
  'label',
  'platform',
  'artist_name',
  'track_name',
  'isrc',
  'quantity',
  'total_gross',
  'total_net',
  'client_share',
  'fees',
  'upc',
  'currency',
  'is_sharein',
  'sale_type',
];

const WORK_TARGET_FIELDS = [
  'gross_to_be_distributed',
  'share_to_be_paid',
  'royaties_gross',
  'admin_fee',
  'royalties_to_be_paid',
  'total_units',
  'song_owners',
  'song_title',
  'currency',
  'bo_songcode',
  'start_date',
  'end_date',
  'sales_channel',
  'country_of_sale',
  'publisher',
  'source',
  'performer',
  'orb_code',
];

const TRANSFORMS = [
  '',
  'uppercase',
  'integer',
  'decimal',
  'client_share',
  'date',
];

const EMPTY_MAPPING = {
  target_field: '',
  mapping_type: 'DIRECT',
  source_column: '',
  fallback_columns: '',
  condition_if_equals: '',
  condition_then_column: '',
  condition_else_column: '',
  fixed_value: '',
  transform: '',
  required: false,
};

const INITIAL_FORM = {
  name: '',
  rights_type: 'WORK',
  source_id: '',
  active: true,
  file_type: '',
  sheet_name: '',
  header_row: 1,
  decimal_separator: '.',
  date_format: '',
  currency_mode: 'COLUMN',
  fixed_currency: '',
  value_is_net: false,
  fields: [],
};

const modalStyle = {
  position: 'absolute',
  top: '3%',
  left: '5%',
  width: '90%',
  maxHeight: '94vh',
  overflowY: 'auto',
  backgroundColor: '#fff',
  boxShadow: 24,
  borderRadius: '4px',
  padding: '24px',
};

const ReportModels = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [rightsFilter, setRightsFilter] = useState('');
  const [form, setForm] = useState({ ...INITIAL_FORM });

  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    setLoading(true);

    try {
      const response = await ORBApi.listPaymentReportSourceModels();
      setModels(Array.isArray(response?.models) ? response.models : []);
    } catch (error) {
      console.error(error);
      toast.error('Falha ao carregar modelos de relatório.');
    } finally {
      setLoading(false);
    }
  };

  const filteredModels = useMemo(() => {
    if (!rightsFilter) {
      return models;
    }

    return models.filter(
      (model) => model.rights_type === rightsFilter,
    );
  }, [models, rightsFilter]);

  const sourceSuggestions = useMemo(() => {
    const sourceMap = new Map();

    models
      .filter((model) => model.rights_type === form.rights_type)
      .forEach((model) => {
        if (!model.source_id) {
          return;
        }

        const key = String(model.source_id);

        if (!sourceMap.has(key)) {
          sourceMap.set(key, {
            id: model.source_id,
            name: model.source_name || `Fonte ${model.source_id}`,
          });
        }
      });

    return Array.from(sourceMap.values());
  }, [models, form.rights_type]);

  const selectedSheet = useMemo(() => {
    if (!previewData?.sheets?.length) {
      return null;
    }

    return (
      previewData.sheets.find(
        (sheet) => (sheet.name || '') === (form.sheet_name || ''),
      ) || previewData.sheets[0]
    );
  }, [previewData, form.sheet_name]);

  const selectedCandidate = useMemo(() => {
    if (!selectedSheet) {
      return null;
    }

    return (
      selectedSheet.header_candidates?.find(
        (candidate) =>
          Number(candidate.row) === Number(form.header_row),
      ) || null
    );
  }, [selectedSheet, form.header_row]);

  const detectedColumns = useMemo(() => {
    if (!selectedSheet) {
      return [];
    }

    return selectedCandidate?.columns || selectedSheet.columns || [];
  }, [selectedSheet, selectedCandidate]);

  const previewRows = useMemo(() => {
    if (!selectedSheet) {
      return [];
    }

    return selectedCandidate?.preview || selectedSheet.preview || [];
  }, [selectedSheet, selectedCandidate]);

  const targetFields =
    form.rights_type === 'TRACK'
      ? TRACK_TARGET_FIELDS
      : WORK_TARGET_FIELDS;

  const updateForm = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const resetForm = () => {
    setForm({ ...INITIAL_FORM });
    setPreviewData(null);
    setSelectedFile(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    if (saving) {
      return;
    }

    setShowCreateModal(false);
    resetForm();
  };

  const addMapping = () => {
    setForm((current) => ({
      ...current,
      fields: [
        ...current.fields,
        { ...EMPTY_MAPPING },
      ],
    }));
  };

  const removeMapping = (index) => {
    setForm((current) => ({
      ...current,
      fields: current.fields.filter(
        (_, currentIndex) => currentIndex !== index,
      ),
    }));
  };

  const updateMapping = (index, key, value) => {
    setForm((current) => ({
      ...current,
      fields: current.fields.map((field, currentIndex) =>
        currentIndex === index
          ? {
              ...field,
              [key]: value,
            }
          : field,
      ),
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;

    setSelectedFile(file);
    setPreviewData(null);

    if (!file) {
      updateForm('file_type', '');
      return;
    }

    const extension =
      file.name.split('.').pop()?.toLowerCase() || '';

    updateForm('file_type', extension);
  };

  const handlePreview = async () => {
    if (!selectedFile) {
      toast.error('Selecione um arquivo de exemplo.');
      return;
    }

    setPreviewing(true);

    try {
      const data =
        await ORBApi.previewPaymentReportSourceModel(selectedFile);

      const firstSheet = data?.sheets?.[0] || null;

      setPreviewData(data);

      setForm((current) => ({
        ...current,
        file_type: data?.file_type || current.file_type,
        sheet_name: firstSheet?.name || '',
        header_row: firstSheet?.suggested_header_row || 1,
        fields: [],
      }));

      toast.success('Arquivo analisado com sucesso.');
    } catch (error) {
      console.error(error);

      const message =
        error?.response?.data?.description ||
        error?.response?.data?.message ||
        'Falha ao analisar arquivo.';

      toast.error(message);
    } finally {
      setPreviewing(false);
    }
  };

  const handleSheetChange = (sheetName) => {
    const sheet =
      previewData?.sheets?.find(
        (item) => (item.name || '') === (sheetName || ''),
      ) || null;

    setForm((current) => ({
      ...current,
      sheet_name: sheetName,
      header_row: sheet?.suggested_header_row || 1,
      fields: [],
    }));
  };

  const buildMappingsPayload = () => {
    return form.fields.map((field, index) => {
      const mapping = {
        target_field: field.target_field,
        mapping_type: field.mapping_type,
        required: Boolean(field.required),
        position: index,
      };

      if (field.transform) {
        mapping.transform = field.transform;
      }

      if (
        field.mapping_type === 'DIRECT' ||
        field.mapping_type === 'FALLBACK' ||
        field.mapping_type === 'CONDITIONAL'
      ) {
        mapping.source_column =
          field.source_column || null;
      }

      if (field.mapping_type === 'FALLBACK') {
        mapping.fallback_columns = field.fallback_columns
          .split(',')
          .map((column) => column.trim())
          .filter(Boolean);
      }

      if (field.mapping_type === 'CONDITIONAL') {
        mapping.condition_config = {
          if_equals: field.condition_if_equals,
          then_column: field.condition_then_column,
          else_column: field.condition_else_column,
        };
      }

      if (field.mapping_type === 'FIXED') {
        mapping.fixed_value = field.fixed_value;
      }

      return mapping;
    });
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      return 'Informe o nome do modelo.';
    }

    if (!form.source_id || Number(form.source_id) <= 0) {
      return 'Informe um ID de fonte válido.';
    }

    if (!form.file_type) {
      return 'Analise um arquivo de exemplo antes de salvar.';
    }

    if (!['csv', 'xls', 'xlsx'].includes(form.file_type)) {
      return 'O arquivo deve ser CSV, XLS ou XLSX.';
    }

    if (!form.fields.length) {
      return 'Adicione pelo menos um mapeamento.';
    }

    const targets = new Set();

    for (const field of form.fields) {
      if (!field.target_field) {
        return 'Todo mapeamento precisa de um campo de destino.';
      }

      if (targets.has(field.target_field)) {
        return `O campo ${field.target_field} está repetido.`;
      }

      targets.add(field.target_field);

      if (
        field.mapping_type === 'DIRECT' &&
        !field.source_column
      ) {
        return `${field.target_field}: selecione a coluna de origem.`;
      }

      if (field.mapping_type === 'FALLBACK') {
        const fallbacks = field.fallback_columns
          .split(',')
          .map((column) => column.trim())
          .filter(Boolean);

        if (!field.source_column && !fallbacks.length) {
          return `${field.target_field}: configure a origem ou fallback.`;
        }
      }

      if (field.mapping_type === 'CONDITIONAL') {
        if (!field.source_column) {
          return `${field.target_field}: selecione a coluna usada na condição.`;
        }

        if (
          field.condition_if_equals === '' ||
          !field.condition_then_column ||
          !field.condition_else_column
        ) {
          return `${field.target_field}: complete a regra condicional.`;
        }
      }

      if (
        field.mapping_type === 'FIXED' &&
        field.fixed_value === ''
      ) {
        return `${field.target_field}: informe o valor fixo.`;
      }
    }

    if (
      form.currency_mode === 'COLUMN' &&
      !targets.has('currency')
    ) {
      return 'Moeda por coluna exige um mapeamento para currency.';
    }

    if (form.currency_mode === 'FIXED') {
      const fixedCurrency =
        form.fixed_currency.trim().toUpperCase();

      if (!/^[A-Z]{3}$/.test(fixedCurrency)) {
        return 'A moeda fixa deve ter exatamente 3 letras, por exemplo USD.';
      }
    }

    return null;
  };

  const handleSave = async () => {
    const validationError = validateForm();

    if (validationError) {
      toast.error(validationError);
      return;
    }

    const payload = {
      name: form.name.trim(),
      rights_type: form.rights_type,
      source_id: Number(form.source_id),
      active: Boolean(form.active),
      file_type: form.file_type,
      sheet_name:
        form.file_type === 'csv'
          ? null
          : form.sheet_name || null,
      header_row: Number(form.header_row),
      decimal_separator: form.decimal_separator,
      date_format: form.date_format.trim() || null,
      currency_mode: form.currency_mode,
      fixed_currency:
        form.currency_mode === 'FIXED'
          ? form.fixed_currency.trim().toUpperCase()
          : null,
      value_is_net: Boolean(form.value_is_net),
      fields: buildMappingsPayload(),
    };

    setSaving(true);

    try {
      await ORBApi.createPaymentReportSourceModel(payload);

      toast.success('Modelo criado com sucesso.');

      setShowCreateModal(false);
      resetForm();
      await loadModels();
    } catch (error) {
      console.error(error);

      const message =
        error?.response?.data?.description ||
        error?.response?.data?.message ||
        'Falha ao criar modelo de relatório.';

      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (model) => {
    try {
      await ORBApi.updatePaymentReportSourceModel(
        model.id,
        {
          active: !model.active,
        },
      );

      toast.success(
        model.active
          ? 'Modelo desativado com sucesso.'
          : 'Modelo ativado com sucesso.',
      );

      await loadModels();
    } catch (error) {
      console.error(error);
      toast.error('Falha ao atualizar o modelo.');
    }
  };

  return (
    <div id="report-models-page">
      <ORBNavBar
        page_title="Admin > Modelos de Relatório"
        selectedOption="drop_admin"
      />

      <Box
        sx={{
          width: '95%',
          margin: '0 auto 40px auto',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          sx={{ marginBottom: 2 }}
        >
          <Button
            variant="contained"
            onClick={openCreateModal}
          >
            Novo modelo
          </Button>

          <FormControl
            size="small"
            sx={{ minWidth: 220 }}
          >
            <InputLabel>Tipo de direito</InputLabel>

            <Select
              label="Tipo de direito"
              value={rightsFilter}
              onChange={(event) =>
                setRightsFilter(event.target.value)
              }
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="WORK">WORK / Autoral</MenuItem>
              <MenuItem value="TRACK">TRACK / Conexo</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <Paper>
          {loading ? (
            <Box
              sx={{
                padding: 4,
                textAlign: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell>Direito</TableCell>
                    <TableCell>Fonte</TableCell>
                    <TableCell>Versão</TableCell>
                    <TableCell>Arquivo</TableCell>
                    <TableCell>Aba</TableCell>
                    <TableCell>Campos</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredModels.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        align="center"
                      >
                        Nenhum modelo encontrado.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredModels.map((model) => (
                      <TableRow key={model.id}>
                        <TableCell>{model.id}</TableCell>
                        <TableCell>{model.name}</TableCell>
                        <TableCell>{model.rights_type}</TableCell>
                        <TableCell>
                          {model.source_name ||
                            `Fonte ${model.source_id}`}
                        </TableCell>
                        <TableCell>
                          {model.version}
                        </TableCell>
                        <TableCell>
                          {(model.file_type || '').toUpperCase()}
                        </TableCell>
                        <TableCell>
                          {model.sheet_name || '-'}
                        </TableCell>
                        <TableCell>
                          {Array.isArray(model.fields)
                            ? model.fields.length
                            : 0}
                        </TableCell>
                        <TableCell>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(model.active)}
                                onChange={() =>
                                  handleToggleActive(model)
                                }
                              />
                            }
                            label={
                              model.active
                                ? 'Ativo'
                                : 'Inativo'
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      <Modal
        open={showCreateModal}
        onClose={closeCreateModal}
      >
        <Box sx={modalStyle}>
          <Typography
            variant="h6"
            sx={{ marginBottom: 2 }}
          >
            Novo modelo de relatório
          </Typography>

          <Alert
            severity="info"
            sx={{ marginBottom: 2 }}
          >
            Selecione um arquivo de exemplo. O sistema
            detectará abas, cabeçalho, colunas e uma amostra
            dos dados. O arquivo de exemplo não será salvo.
          </Alert>

          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Nome do modelo"
              value={form.name}
              onChange={(event) =>
                updateForm('name', event.target.value)
              }
            />

            <FormControl fullWidth>
              <InputLabel>Tipo de direito</InputLabel>

              <Select
                label="Tipo de direito"
                value={form.rights_type}
                onChange={(event) => {
                  setForm((current) => ({
                    ...current,
                    rights_type: event.target.value,
                    source_id: '',
                    fields: [],
                  }));
                }}
              >
                <MenuItem value="WORK">
                  WORK / Autoral
                </MenuItem>
                <MenuItem value="TRACK">
                  TRACK / Conexo
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="number"
              label="ID da fonte"
              value={form.source_id}
              onChange={(event) =>
                updateForm(
                  'source_id',
                  event.target.value,
                )
              }
              helperText={
                sourceSuggestions.length
                  ? `Fontes já usadas: ${sourceSuggestions
                      .map(
                        (source) =>
                          `${source.name} (ID ${source.id})`,
                      )
                      .join(' | ')}`
                  : 'Informe o ID da fonte cadastrada no sistema.'
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={form.active}
                  onChange={(event) =>
                    updateForm(
                      'active',
                      event.target.checked,
                    )
                  }
                />
              }
              label="Criar modelo como ativo"
            />

            <Paper
              variant="outlined"
              sx={{ padding: 2 }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', md: 'center' }}
              >
                <Button
                  component="label"
                  variant="outlined"
                >
                  Selecionar arquivo
                  <input
                    hidden
                    type="file"
                    accept=".csv,.xls,.xlsx"
                    onChange={handleFileChange}
                  />
                </Button>

                <Typography
                  variant="body2"
                  sx={{ flexGrow: 1 }}
                >
                  {selectedFile
                    ? selectedFile.name
                    : 'Nenhum arquivo selecionado'}
                </Typography>

                <Button
                  variant="contained"
                  disabled={
                    !selectedFile ||
                    previewing
                  }
                  onClick={handlePreview}
                >
                  {previewing
                    ? 'Analisando...'
                    : 'Analisar arquivo'}
                </Button>
              </Stack>
            </Paper>

            {previewData ? (
              <>
                <TextField
                  fullWidth
                  label="Tipo de arquivo"
                  value={
                    (form.file_type || '').toUpperCase()
                  }
                  disabled
                />

                {form.file_type !== 'csv' ? (
                  <FormControl fullWidth>
                    <InputLabel>Aba</InputLabel>

                    <Select
                      label="Aba"
                      value={form.sheet_name}
                      onChange={(event) =>
                        handleSheetChange(
                          event.target.value,
                        )
                      }
                    >
                      {(previewData.sheets || []).map(
                        (sheet) => (
                          <MenuItem
                            key={sheet.name}
                            value={sheet.name}
                          >
                            {sheet.name}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                  </FormControl>
                ) : null}

                <FormControl fullWidth>
                  <InputLabel>Linha do cabeçalho</InputLabel>

                  <Select
                    label="Linha do cabeçalho"
                    value={form.header_row}
                    onChange={(event) =>
                      updateForm(
                        'header_row',
                        event.target.value,
                      )
                    }
                  >
                    {(
                      selectedSheet?.header_candidates || []
                    ).map((candidate) => (
                      <MenuItem
                        key={candidate.row}
                        value={candidate.row}
                      >
                        Linha {candidate.row} - score{' '}
                        {candidate.score}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Typography variant="subtitle1">
                  Preview
                </Typography>

                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ maxHeight: 260 }}
                >
                  <Table
                    size="small"
                    stickyHeader
                  >
                    <TableHead>
                      <TableRow>
                        {detectedColumns.map((column) => (
                          <TableCell
                            key={`${column.index}-${column.name}`}
                          >
                            {column.name}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {previewRows.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                          {detectedColumns.map((column) => (
                            <TableCell
                              key={`${rowIndex}-${column.index}-${column.name}`}
                            >
                              {row?.[column.name] === null ||
                              row?.[column.name] === undefined
                                ? ''
                                : String(
                                    row[column.name],
                                  )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <FormControl fullWidth>
                  <InputLabel>Separador decimal</InputLabel>

                  <Select
                    label="Separador decimal"
                    value={form.decimal_separator}
                    onChange={(event) =>
                      updateForm(
                        'decimal_separator',
                        event.target.value,
                      )
                    }
                  >
                    <MenuItem value=".">
                      Ponto (.)
                    </MenuItem>
                    <MenuItem value=",">
                      Vírgula (,)
                    </MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Formato de data"
                  value={form.date_format}
                  onChange={(event) =>
                    updateForm(
                      'date_format',
                      event.target.value,
                    )
                  }
                  placeholder="%Y-%m-%d"
                  helperText="Deixe vazio quando não for necessário informar um formato específico."
                />

                <FormControl fullWidth>
                  <InputLabel>Modo da moeda</InputLabel>

                  <Select
                    label="Modo da moeda"
                    value={form.currency_mode}
                    onChange={(event) =>
                      updateForm(
                        'currency_mode',
                        event.target.value,
                      )
                    }
                  >
                    <MenuItem value="COLUMN">
                      Moeda vem de uma coluna
                    </MenuItem>
                    <MenuItem value="FIXED">
                      Moeda fixa
                    </MenuItem>
                  </Select>
                </FormControl>

                {form.currency_mode === 'FIXED' ? (
                  <TextField
                    fullWidth
                    label="Moeda fixa"
                    value={form.fixed_currency}
                    onChange={(event) =>
                      updateForm(
                        'fixed_currency',
                        event.target.value.toUpperCase(),
                      )
                    }
                    inputProps={{
                      maxLength: 3,
                    }}
                    placeholder="USD"
                  />
                ) : null}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.value_is_net}
                      onChange={(event) =>
                        updateForm(
                          'value_is_net',
                          event.target.checked,
                        )
                      }
                    />
                  }
                  label="O valor do arquivo já é líquido"
                />

                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Box>
                    <Typography variant="h6">
                      Mapeamentos
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Relacione as colunas do arquivo aos
                      campos internos do ORB.
                    </Typography>
                  </Box>

                  <Button
                    variant="outlined"
                    onClick={addMapping}
                  >
                    Adicionar campo
                  </Button>
                </Stack>

                {form.fields.length === 0 ? (
                  <Alert severity="warning">
                    Adicione pelo menos um campo antes de
                    salvar o modelo.
                  </Alert>
                ) : null}

                {form.fields.map((field, index) => (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{ padding: 2 }}
                  >
                    <Stack spacing={2}>
                      <FormControl fullWidth>
                        <InputLabel>
                          Campo de destino
                        </InputLabel>

                        <Select
                          label="Campo de destino"
                          value={field.target_field}
                          onChange={(event) =>
                            updateMapping(
                              index,
                              'target_field',
                              event.target.value,
                            )
                          }
                        >
                          {targetFields.map((target) => (
                            <MenuItem
                              key={target}
                              value={target}
                              disabled={form.fields.some(
                                (
                                  currentField,
                                  currentIndex,
                                ) =>
                                  currentIndex !== index &&
                                  currentField.target_field ===
                                    target,
                              )}
                            >
                              {target}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl fullWidth>
                        <InputLabel>
                          Tipo de mapeamento
                        </InputLabel>

                        <Select
                          label="Tipo de mapeamento"
                          value={field.mapping_type}
                          onChange={(event) =>
                            updateMapping(
                              index,
                              'mapping_type',
                              event.target.value,
                            )
                          }
                        >
                          <MenuItem value="DIRECT">
                            DIRECT
                          </MenuItem>
                          <MenuItem value="FALLBACK">
                            FALLBACK
                          </MenuItem>
                          <MenuItem value="CONDITIONAL">
                            CONDITIONAL
                          </MenuItem>
                          <MenuItem value="FIXED">
                            FIXED
                          </MenuItem>
                        </Select>
                      </FormControl>

                      {field.mapping_type !== 'FIXED' ? (
                        <FormControl fullWidth>
                          <InputLabel>
                            Coluna de origem
                          </InputLabel>

                          <Select
                            label="Coluna de origem"
                            value={field.source_column}
                            onChange={(event) =>
                              updateMapping(
                                index,
                                'source_column',
                                event.target.value,
                              )
                            }
                          >
                            <MenuItem value="">
                              Nenhuma
                            </MenuItem>

                            {detectedColumns.map(
                              (column) => (
                                <MenuItem
                                  key={`${column.index}-${column.name}`}
                                  value={column.name}
                                >
                                  {column.name}
                                </MenuItem>
                              ),
                            )}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField
                          fullWidth
                          label="Valor fixo"
                          value={field.fixed_value}
                          onChange={(event) =>
                            updateMapping(
                              index,
                              'fixed_value',
                              event.target.value,
                            )
                          }
                        />
                      )}

                      {field.mapping_type === 'FALLBACK' ? (
                        <TextField
                          fullWidth
                          label="Colunas fallback"
                          value={field.fallback_columns}
                          onChange={(event) =>
                            updateMapping(
                              index,
                              'fallback_columns',
                              event.target.value,
                            )
                          }
                          helperText="Separe os nomes das colunas por vírgula."
                        />
                      ) : null}

                      {field.mapping_type ===
                      'CONDITIONAL' ? (
                        <>
                          <TextField
                            fullWidth
                            label="Se o valor for igual a"
                            value={
                              field.condition_if_equals
                            }
                            onChange={(event) =>
                              updateMapping(
                                index,
                                'condition_if_equals',
                                event.target.value,
                              )
                            }
                          />

                          <FormControl fullWidth>
                            <InputLabel>
                              Então usar coluna
                            </InputLabel>

                            <Select
                              label="Então usar coluna"
                              value={
                                field.condition_then_column
                              }
                              onChange={(event) =>
                                updateMapping(
                                  index,
                                  'condition_then_column',
                                  event.target.value,
                                )
                              }
                            >
                              {detectedColumns.map(
                                (column) => (
                                  <MenuItem
                                    key={`then-${column.index}-${column.name}`}
                                    value={column.name}
                                  >
                                    {column.name}
                                  </MenuItem>
                                ),
                              )}
                            </Select>
                          </FormControl>

                          <FormControl fullWidth>
                            <InputLabel>
                              Senão usar coluna
                            </InputLabel>

                            <Select
                              label="Senão usar coluna"
                              value={
                                field.condition_else_column
                              }
                              onChange={(event) =>
                                updateMapping(
                                  index,
                                  'condition_else_column',
                                  event.target.value,
                                )
                              }
                            >
                              {detectedColumns.map(
                                (column) => (
                                  <MenuItem
                                    key={`else-${column.index}-${column.name}`}
                                    value={column.name}
                                  >
                                    {column.name}
                                  </MenuItem>
                                ),
                              )}
                            </Select>
                          </FormControl>
                        </>
                      ) : null}

                      <FormControl fullWidth>
                        <InputLabel>
                          Transformação
                        </InputLabel>

                        <Select
                          label="Transformação"
                          value={field.transform}
                          onChange={(event) =>
                            updateMapping(
                              index,
                              'transform',
                              event.target.value,
                            )
                          }
                        >
                          {TRANSFORMS.map((transform) => (
                            <MenuItem
                              key={transform || 'none'}
                              value={transform}
                            >
                              {transform || 'Nenhuma'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.required}
                            onChange={(event) =>
                              updateMapping(
                                index,
                                'required',
                                event.target.checked,
                              )
                            }
                          />
                        }
                        label="Campo obrigatório"
                      />

                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() =>
                          removeMapping(index)
                        }
                      >
                        Remover campo
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </>
            ) : null}

            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
            >
              <Button
                variant="outlined"
                disabled={saving}
                onClick={closeCreateModal}
              >
                Cancelar
              </Button>

              <Button
                variant="contained"
                disabled={saving || !previewData}
                onClick={handleSave}
              >
                {saving
                  ? 'Salvando...'
                  : 'Salvar modelo'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>
    </div>
  );
};

export default ReportModels;
